import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, HStack, Text, Badge, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import type { ActiveScenarioDto } from '../types';

interface SortableScenarioListProps {
  scenarios: ActiveScenarioDto[];
  onReorder: (orderedScenarioIds: string[]) => void;
}

function Row({ scenario }: { scenario: ActiveScenarioDto }) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: scenario.scenarioId,
  });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  return (
    <HStack
      ref={setNodeRef}
      style={style}
      py={2.5}
      px={3}
      spacing={3}
      bg="white"
      borderWidth="1px"
      borderColor={scenario.isWinner ? 'green.200' : 'gray.100'}
      borderRadius="md"
    >
      <Box {...attributes} {...listeners} cursor="grab" color="gray.300" fontSize="lg" lineHeight={1} _hover={{ color: 'gray.500' }}>
        ⠿
      </Box>
      <Box flex={1} minW={0}>
        <Text fontWeight="500" color="gray.800" noOfLines={1}>
          {scenario.scenarioName}
        </Text>
        <Text fontSize="xs" color="gray.400">
          {t('schedule.playlistCount', { count: scenario.playlistCount })}
        </Text>
      </Box>
      {scenario.isWinner ? (
        <Badge colorScheme="green">{t('schedule.playing')}</Badge>
      ) : (
        <Badge colorScheme="gray">{t('schedule.overridden')}</Badge>
      )}
    </HStack>
  );
}

export function SortableScenarioList({ scenarios, onReorder }: SortableScenarioListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = scenarios.map((s) => s.scenarioId);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(ids, oldIndex, newIndex));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <VStack align="stretch" spacing={2}>
          {scenarios.map((s) => (
            <Row key={s.scenarioId} scenario={s} />
          ))}
        </VStack>
      </SortableContext>
    </DndContext>
  );
}

export default SortableScenarioList;
